import toast from 'react-hot-toast';
import { DataDiff } from './data-diff.jsx';
import { Icon } from '@iconify-icon/react';
import { GalleryDiff } from './gallery-diff.jsx';
import { checkIfDiffExist } from './helpers.js';
import { RowDiff } from './row-diff.jsx';

export function DetailDiff(props) {
    const { incomingPlan, plan, updatedPlan, incomingDocs, onEdit, dayId, itineraryDocuments, places } = props;

    const diffExists = checkIfDiffExist(incomingPlan, plan, 'plan');

    const incomingDocuments = incomingPlan?.documents ?? [];
    const filteredIncomingDocs = incomingDocuments.filter((doc) => !plan.documents.some((d) => d === doc));

    const hasIncomingDocs = filteredIncomingDocs && filteredIncomingDocs.length > 0;
    const allDocumentsArePresentInItinerary = filteredIncomingDocs.every((doc) =>
        itineraryDocuments.some((d) => d.id === doc)
    );

    const addDocumentInPlan = (id) => {
        onEdit([dayId, plan?.id], 'documents', [...plan.documents, id]);
        toast.success(_t.edit.updateItinerary.addedDocument('plan'));
    };

    const removeDocumentFromPlan = (id) => {
        const newDocuments = plan.documents.filter((doc) => doc !== id);
        onEdit([dayId, plan?.id], 'documents', newDocuments);
        toast.success(_t.edit.updateItinerary.removeDocument('plan'));
    };

    const incomingRooms = incomingPlan?.rooms ?? [];
    const updatedRooms = updatedPlan.rooms?.filter((room) => incomingRooms.find((r) => r.id === room.id));
    const toUpdateRooms = incomingRooms?.filter((room) => plan.rooms.find((r) => r.id === room.id));
    const newRooms = incomingRooms?.filter((room) => !plan?.rooms.find((r) => r.id === room.id));

    let roomsToUpdateExist;

    plan?.rooms?.forEach((room) => {
        const incomingRoom = incomingRooms.find((r) => r.importId === room.importId);
        roomsToUpdateExist = checkIfDiffExist(incomingRoom, room, 'room');
    });

    return (
        <div className="flex flex-col items-center gap-4 h-full">
            {!diffExists && (
                <p className="w-full col-span-full p-4 bg-neutral-100 rounded-md text-center text-neutral-700 font-semibold">
                    {_t.edit.updateItinerary.noChanges('plan')}
                </p>
            )}
            <div className="flex flex-col gap-4 w-[500px] xl:w-[480px] p-2">
                {incomingPlan && (
                    <>
                        <GalleryDiff
                            incomingGallery={incomingPlan.gallery}
                            currentGallery={plan.gallery}
                            updatedGallery={updatedPlan.gallery}
                            onEdit={onEdit}
                            dayId={dayId}
                            planId={plan?.id}
                        />
                        {hasIncomingDocs && (
                            <div className="contents">
                                <p className="font-semibold">{_t.edit.updateItinerary.incomingDocs()}</p>
                                {!allDocumentsArePresentInItinerary && (
                                    <p className="pb-2 text-sm text-neutral-500">
                                        {_t.edit.updateItinerary.addDocsToItinerary()}
                                    </p>
                                )}
                                <div className="flex flex-col gap-2">
                                    {filteredIncomingDocs.map((docId) => {
                                        const docToAdd = !itineraryDocuments.some((doc) => doc.id === docId);
                                        const docIsPresent = updatedPlan.documents.some((d) => d === docId);

                                        return (
                                            <RowDiff
                                                key={docId}
                                                el={incomingDocs.find((d) => d.id === docId)}
                                                elIsPresent={docIsPresent}
                                                elToAdd={docToAdd}
                                                onUpdateElement={() => addDocumentInPlan(docId)}
                                                onRestoreElement={() => removeDocumentFromPlan(docId)}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <DataDiff
                            incomingData={incomingPlan}
                            currentData={plan}
                            updatedData={updatedPlan}
                            onUpdateData={(key) => {
                                if (updatedPlan[key] === incomingPlan[key]) return;
                                onEdit([dayId, updatedPlan?.id], key, incomingPlan[key]);
                            }}
                            onRestoreData={(key) => {
                                if (updatedPlan[key] === plan[key]) return;
                                onEdit([dayId, updatedPlan?.id], key, plan[key]);
                            }}
                            onRestoreSuggestionPlan={() => {
                                Object.entries(plan).forEach(([key, value]) => {
                                    if (key !== 'id' && key !== 'importId' && key !== 'type' && key !== 'documents') {
                                        if (value !== incomingPlan[key]) {
                                            onEdit([dayId, updatedPlan?.id], key, value);
                                        }
                                    }
                                });
                            }}
                            onReplaceSuggestionPlan={() => {
                                Object.entries(incomingPlan).forEach(([key, value]) => {
                                    if (key !== 'id' && key !== 'importId' && key !== 'type' && key !== 'documents') {
                                        if (value !== updatedPlan[key]) {
                                            onEdit([dayId, updatedPlan?.id], key, value);
                                        }
                                    }
                                });
                            }}
                            isPlan={true}
                            places={places}
                        />
                    </>
                )}
                {newRooms && newRooms.length > 0 && (
                    <>
                        <p className="font-semibold">{_t.edit.updateItinerary.incomingRooms()}</p>
                        {newRooms.map((room) => {
                            const roomIsPresent = updatedPlan.rooms.some((r) => r.importId === room.importId);
                            return (
                                <RowDiff
                                    key={room?.id}
                                    el={incomingPlan.rooms.find((r) => r.id === room.id)}
                                    elIsPresent={roomIsPresent}
                                    onUpdateElement={() =>
                                        onEdit([dayId, plan?.id], 'rooms', [...updatedPlan.rooms, room])
                                    }
                                    onRestoreElement={() => {
                                        const newRooms = updatedPlan.rooms.filter((r) => r.importId !== room.importId);
                                        onEdit([dayId, plan?.id], 'rooms', newRooms);
                                    }}
                                />
                            );
                        })}
                    </>
                )}
                {roomsToUpdateExist && (
                    <div>
                        <p className="text-lg font-semibold">{_t.edit.updateItinerary.roomsToUpdate()}</p>
                        <div>
                            {toUpdateRooms.map((room) => {
                                const currentRoom = plan.rooms.find((r) => r.importId === room.importId);
                                const updatedRoom = updatedRooms.find((r) => r.importId === room.importId);
                                return (
                                    <UpdateRoomDetail
                                        key={room.id}
                                        currentRoom={currentRoom}
                                        updatedRoom={updatedRoom}
                                        incomingRoom={room}
                                        onEdit={onEdit}
                                        dayId={dayId}
                                        planId={updatedPlan.id}
                                        roomId={currentRoom.id}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function UpdateRoomDetail(props) {
    const { currentRoom, updatedRoom, incomingRoom, onEdit, dayId, planId, roomId } = props;

    return (
        <div className="w-full">
            <div className="w-full flex gap-2">
                <Icon icon="material-symbols:bed-outline" width="1.2rem" height="1.2rem" className="pt-[2px]" />
                <p className="w-full font-semibold text-left no-whitespace text-ellipsis overflow-hidden col-span-5 max-h-32 pb-2">
                    {incomingRoom.name}
                </p>
            </div>
            <div className="flex flex-col items-center w-full gap-4 p-2 rounded bg-neutral-100">
                {incomingRoom && (
                    <>
                        <GalleryDiff
                            incomingGallery={incomingRoom.gallery}
                            currentGallery={currentRoom.gallery}
                            updatedGallery={updatedRoom.gallery}
                            onEdit={onEdit}
                            dayId={dayId}
                            planId={planId}
                            roomId={roomId}
                        />
                        <DataDiff
                            incomingData={incomingRoom}
                            currentData={currentRoom}
                            updatedData={updatedRoom}
                            onUpdateData={(key) => {
                                if (updatedRoom[key] === incomingRoom[key]) return;
                                onEdit([dayId, planId, roomId], key, incomingRoom[key]);
                            }}
                            onRestoreData={(key) => {
                                if (currentRoom[key] === incomingRoom[key]) return;
                                onEdit([dayId, planId, roomId], key, currentRoom[key]);
                            }}
                            isPlan={true}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
